import React, { useEffect, useMemo, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import Container from "react-bootstrap/Container";
import Button from "react-bootstrap/Button";
import MenuBar from "../common/MenuPanel";
import FooterPanel from "../common/FooterPanel";
import marianConsecrationPdf from "../../attachments/33 - Day Concecration Mother MAry.pdf";
import marianConsecrationText from "../../attachments/Vimalahrudaya_Prathishta_Converted.txt";

const SHARED_PREPARATION_PAGES = [6, 7, 8, 9];

const DAY_START_PAGES = [
  10, 12, 15, 18, 21, 23, 26, 28, 31, 33, 35,
  38, 41, 44, 46, 49, 51, 53, 56, 59, 62, 65,
  69, 72, 75, 78, 81, 84, 86, 89, 92, 95, 98,
];

const DAY_ENTRIES = DAY_START_PAGES.map((startPage, index) => {
  const nextStartPage = DAY_START_PAGES[index + 1];
  const endPage = nextStartPage ? nextStartPage : 101;
  const pages = [];
  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }

  return {
    id: index + 1,
    title: `Day ${index + 1}`,
    dayPages: pages,
  };
});

function parsePagesByNumber(rawText) {
  const pageMap = new Map();
  const chunks = rawText
    .replace(/\r\n/g, "\n")
    .split(/www\.malankaralibrary\.com\s*/g)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  chunks.forEach((chunk) => {
    const match = chunk.match(/^(\d{1,3})([\s\S]*)$/);
    if (!match) {
      return;
    }

    const pageNumber = Number.parseInt(match[1], 10);
    if (!Number.isFinite(pageNumber)) {
      return;
    }

    const pageContent = (match[2] || "").trim();
    if (pageContent) {
      pageMap.set(pageNumber, pageContent);
    }
  });

  return pageMap;
}

function TextPageBlock({ pageNumber, titlePrefix, content }) {
  return (
    <div className="mb-4">
      <h5 className="mb-3">{titlePrefix} - Page {pageNumber}</h5>
      <div
        style={{
          width: "100%",
          border: "1px solid #d9c7a6",
          borderRadius: "12px",
          background: "#fff",
          padding: "16px",
          lineHeight: 1.8,
          whiteSpace: "pre-wrap",
        }}
      >
        {content || "Content for this page is not available in the extracted text."}
      </div>
    </div>
  );
}

function PdfPageBlock({ pageNumber, titlePrefix }) {
  const pageSrc = `${marianConsecrationPdf}#page=${pageNumber}&zoom=page-width`;

  return (
    <div className="mb-4">
      <h5 className="mb-3">{titlePrefix} - Page {pageNumber}</h5>
      <div
        style={{
          width: "100%",
          border: "1px solid #d9c7a6",
          borderRadius: "12px",
          background: "#fff",
          padding: "16px",
        }}
      >
        <Button href={pageSrc} target="_blank" rel="noopener noreferrer" variant="outline-primary">
          Open PDF - Page {pageNumber}
        </Button>
      </div>
    </div>
  );
}

const MarianConsecrationMalayalamPage = () => {
  const [rawText, setRawText] = useState("");
  const [loadError, setLoadError] = useState("");
  const [viewMode, setViewMode] = useState("pdf");

  useEffect(() => {
    let isMounted = true;

    fetch(marianConsecrationText)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to load the Marian consecration text file.");
        }

        return response.text();
      })
      .then((text) => {
        if (isMounted) {
          setRawText(text);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setLoadError(error.message || "Failed to load text content.");
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const pages = useMemo(() => parsePagesByNumber(rawText), [rawText]);

  return (
    <>
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>

      <div className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          }}
        >
          <div className="banner-heading text-center wow fadeInDown">
            <h1 className="text-white">33 Days Marian Consecration</h1>
            <p className="text-white mb-0">Malayalam Resource</p>
          </div>
        </div>
      </div>

      <Container className="mt-4 mb-5">
        <div className="text-center mb-4">
          <h2>33 Days Marian Consecration in Malayalam</h2>
          <p>
            Each day below is collapsible. Readable PDF pages are shown by default.
          </p>
          <div className="d-flex justify-content-center gap-2 mb-3">
            <Button
              variant={viewMode === "pdf" ? "primary" : "outline-primary"}
              onClick={() => setViewMode("pdf")}
            >
              Readable PDF View
            </Button>
            <Button
              variant={viewMode === "text" ? "primary" : "outline-primary"}
              onClick={() => setViewMode("text")}
            >
              Extracted Text View
            </Button>
          </div>
          <Button
            variant="outline-primary"
            href={marianConsecrationPdf}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Source PDF
          </Button>
        </div>

        {viewMode === "text" && loadError && (
          <div className="alert alert-danger" role="alert">
            {loadError}
          </div>
        )}

        {viewMode === "text" && !loadError && !rawText && (
          <div className="alert alert-info" role="alert">
            Loading Malayalam text content...
          </div>
        )}

        <Accordion alwaysOpen>
          {DAY_ENTRIES.map((day) => (
            <Accordion.Item eventKey={String(day.id)} key={day.id}>
              <Accordion.Header>{day.title}</Accordion.Header>
              <Accordion.Body>
                <div className="mb-4">
                  <h4 className="mb-3">Day Prayer</h4>
                  {day.dayPages.map((pageNumber) => (
                    viewMode === "pdf" ? (
                      <PdfPageBlock
                        key={`day-${day.id}-pdf-page-${pageNumber}`}
                        pageNumber={pageNumber}
                        titlePrefix={day.title}
                      />
                    ) : (
                      <TextPageBlock
                        key={`day-${day.id}-text-page-${pageNumber}`}
                        pageNumber={pageNumber}
                        titlePrefix={day.title}
                        content={pages.get(pageNumber)}
                      />
                    )
                  ))}
                </div>

                <div>
                  <h4 className="mb-3">Preparation Pages 6-9</h4>
                  {SHARED_PREPARATION_PAGES.map((pageNumber) => (
                    viewMode === "pdf" ? (
                      <PdfPageBlock
                        key={`shared-pdf-page-${day.id}-${pageNumber}`}
                        pageNumber={pageNumber}
                        titlePrefix="Preparation"
                      />
                    ) : (
                      <TextPageBlock
                        key={`shared-text-page-${day.id}-${pageNumber}`}
                        pageNumber={pageNumber}
                        titlePrefix="Preparation"
                        content={pages.get(pageNumber)}
                      />
                    )
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </Container>

      <FooterPanel />
    </>
  );
};

export default MarianConsecrationMalayalamPage;